import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomFieldEntityEnum,
  IOfferingProductSchema,
  IOfferingReqFormInput,
  IOfferingReqSchema,
  IProductResSchema,
  IRateReqSchemaUI,
  IRateResBaseSchema,
  OfferingReqSchema,
  OfferingStatusEnum,
  OfferingTypesEnum,
  ProductQuantityDisplayTypeEnum,
  ProductResWithMandatory,
  ProductStatusEnum,
  ProductTypeEnum,
  RateStatusEnum,
  RateTypeEnum,
} from '@monetize/types/app';
import {
  filterObjectBySearchTerm,
  sortByProductPosition,
} from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getAppStore } from '../../../store/appStore';
import { v4 as uuidv4 } from 'uuid';
import { handleApiErrorToast } from '../../../api/axios';
import { doGetOfferingById } from '../../../api/productCatalogService';
import { CustomFieldDataForm } from '../../../components/CustomFields/CustomFieldDataForm';
import {
  MBox,
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MText,
  MTooltip,
} from '../../../components/Monetize';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { ExpandCollapseButton } from '../../../components/Monetize/ExpandCollapseButton';
import MAlert from '../../../components/Monetize/MAlert';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { SearchInput } from '../../../components/Monetize/SearchInput';
import {
  OFFERING_STATUS_DISPLAY,
  OFFERING_STATUS_FORM_OPTIONS,
  defaultOfferingCreateValues,
} from '../../../constants/offerings';
import { STATUS_PLACEHOLDER } from '../../../constants/placeholders';
import {
  ONETIME_TOKEN_PRODUCT_TYPES,
  ONE_TIME_PRODUCT_TYPES,
  RECURRING_TOKEN_PRODUCT_TYPES,
  SUBSCRIPTION_PRODUCT_TYPES,
} from '../../../constants/products';
import { OFFERING_LIST, getOfferingEditRoute } from '../../../constants/routes';
import { useOffering } from '../../../hooks';
import { useCtrlEnterHotkey } from '../../../hooks/useHotkeys';
import { usePrompt } from '../../../hooks/usePrompt';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  activeToastState,
  offeringRateFormSortValueState,
} from '../../../store/global.store';
import { convertFormValuesToReq } from '../../../utils/customFields';
import { getHeaderTitle } from '../../../utils/productCatalog';
import { ImportRateModal } from './components/importRates/ImportRateModal';
import { OfferingActions } from './components/OfferingActions';
import OfferingTypeInput from './components/OfferingTypeInput';
import ProductsTable from './components/ProductsTable';
import RateFormProvider, {
  type RateFormRef,
} from './components/Rate/RateFormProvider';
import {
  OfferingFormRateSortButton,
  RateSortKey,
  sortRatesByFilterValue,
} from './OfferingFormRateSortButton';

export const OfferingForm = () => {
  const navigate = useNavigate();
  const { offeringId: id = '' } = useParams();
  const {
    offering,
    error,
    createOffering,
    updateOffering,
    fetchOffering,
    loading,
    isFetching,
    isError,
  } = useOffering(id);

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    reset,
    register,
    formState: { errors, isDirty, dirtyFields, isSubmitted, isValid },
  } = useForm<IOfferingReqFormInput, unknown, IOfferingReqSchema>({
    mode: 'onChange',
    resolver: zodResolver(OfferingReqSchema),
    defaultValues: {
      ...defaultOfferingCreateValues,
    },
  });

  const startDateField = watch('startDate');
  const offeringNameField = watch('name');
  const minEndDate = useMemo(
    () =>
      startDateField
        ? addDays(startOfDay(parseISO(startDateField)), 1)
        : undefined,
    [startDateField],
  );
  register('offeringProducts');

  const rateFormRefs = useRef<Record<string, RateFormRef | null>>({});
  // Stored in ref so we can use this without causing re-renders - changes to this do not trigger sort
  const searchTermRef = useRef('');
  const [products, setProducts] = useState<ProductResWithMandatory[]>([]);
  const [productsEdited, setProductsEdited] = useState(false);
  const [rateIds, setRateIds] = useState<string[]>([]);
  const [visibleRateIds, setVisibleRateIds] = useState<string[]>([]);
  const [inactiveRateIds, setInactiveRateIds] = useState<string[]>([]);
  const [rateTempUUIds, setRateTempUUIds] = useState<string[]>([]); // to handle unsaved tempUUIds because we can't update rateIds all the time. Changes of rateIds causes rates loading
  const [editable, setEditable] = useState(true);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [rateCloneData, setRateCloneData] = useState<
    Record<string, IRateReqSchemaUI>
  >({});
  const [dirtyRateIds, setDirtyRateIds] = useState<string[]>([]);
  const [showInactiveRate, setShowInactiveRate] = useState(false);
  const [isExpandedAll, setIsExpandedAll] = useState(false);
  const [isAllProductMandatory, setIsAllProductMandatory] = useState(false);
  const [selectedSortItem, setSelectedSortItem] = useAtom(
    offeringRateFormSortValueState,
  );
  const [
    copiedOfferingHasInactiveProducts,
    setCopiedOfferingHasInactiveProducts,
  ] = useState(false);
  const [productBucket, setProductBucket] = useState<string>();
  const isFirstLoad = useRef(true);

  // workaround to avoid re-renders but to allow usage within useEffect
  const rateIdsRef = useRef(rateIds);
  const rateTempUUIdsRef = useRef(rateTempUUIds);
  const selectedSortItemRef = useRef(selectedSortItem);
  rateIdsRef.current = rateIds;
  rateTempUUIdsRef.current = rateTempUUIds;
  selectedSortItemRef.current = selectedSortItem;

  usePrompt(
    'There are unsaved changes, do you want to discard these changes?',
    !loading && !isFetching && (isDirty || !!dirtyRateIds.length),
  );

  const offeringType = getValues('type');

  useEffect(() => {
    if (products.length) {
      const productWithBucket = products.find((product) => product.bucket);
      if (productWithBucket?.id) {
        setProductBucket(productWithBucket?.id);
      }
    }
  }, [products]);

  const productsMatchOfferingType = (
    prods: IProductResSchema[],
    type: OfferingTypesEnum,
  ) => {
    if (prods.length === 0) {
      return false;
    }
    const hasSubscriptionProducts = prods.some(({ productType }) => {
      return SUBSCRIPTION_PRODUCT_TYPES.has(productType);
    });

    const onlyOneTime = prods.every(({ productType }) => {
      return ONE_TIME_PRODUCT_TYPES.has(productType);
    });

    const onlyMinCommitProduct = prods.every(
      ({ productType }) => productType === ProductTypeEnum.MIN_COMMIT,
    );

    const onlyPoTProduct =
      prods.length === 1 &&
      (prods[0].productType === ProductTypeEnum.ADVANCE ||
        prods[0].productType === ProductTypeEnum.ONETIME);

    const hasTokenProducts = prods.every(({ productType }) => {
      return RECURRING_TOKEN_PRODUCT_TYPES.has(productType);
    });

    const hasOnlyOnetimeTokenProducts = prods.every(({ productType }) => {
      return ONETIME_TOKEN_PRODUCT_TYPES.has(productType);
    });

    switch (type) {
      case OfferingTypesEnum.SUBSCRIPTION:
        return hasSubscriptionProducts && !onlyOneTime;
      case OfferingTypesEnum.ONETIME:
        return onlyOneTime;
      case OfferingTypesEnum.MIN_COMMIT:
        return onlyMinCommitProduct;
      case OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL:
        return onlyPoTProduct;
      case OfferingTypesEnum.TOKEN:
        return hasTokenProducts && !hasOnlyOnetimeTokenProducts;
      case OfferingTypesEnum.ONETIME_TOKEN:
        return hasOnlyOnetimeTokenProducts;
      default:
        return false;
    }
  };

  const prepareOfferingPayload = (data: IOfferingReqFormInput) => {
    return {
      ...data,
      customFields: convertFormValuesToReq(data.customFields),
      offeringProducts: products.map((product, index) => ({
        productId: product.id,
        isMandatory: product.isMandatory,
        quantityDisplayType:
          product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
        isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
        position: index + 1,
      })),
    };
  };

  const onSubmit = async (data: IOfferingReqFormInput) => {
    const payload = prepareOfferingPayload(data);

    if (offering?.id) {
      // update existing offering
      await updateOffering({ id: offering.id, payload });
    } else {
      // create new offering
      const res = await createOffering(payload);
      if (res) {
        navigate(getOfferingEditRoute(res.id), { replace: true });
      }
    }

    // reset form and local state after successful save
    reset(payload);
    setProductsEdited(false);
  };

  const handleSaveButtonPress = () => {
    handleSubmit(onSubmit)();
  };

  const handleUpdateAdvancedSettingsProducts = useCallback(
    (updatedProducts: ProductResWithMandatory[]) => {
      setProducts(updatedProducts);
      setValue(
        'offeringProducts',
        updatedProducts.map((product, index) => ({
          productId: product.id,
          isMandatory: product.isMandatory,
          quantityDisplayType:
            product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
          isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
          position: index + 1,
        })),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    },
    [setValue],
  );

  const checkIfAllProductsAreMandatory = (
    productsToCheck: ProductResWithMandatory[],
  ) => {
    setIsAllProductMandatory(
      productsToCheck.every(({ isMandatory }) => isMandatory),
    );
  };

  const handleProductSelect = (currentProduct: ProductResWithMandatory) => {
    const doesProductExist = products.find(
      (product) => product.id === currentProduct.id,
    );
    setProductBucket(currentProduct?.bucket?.id);
    if (!doesProductExist) {
      const nextProducts = [
        ...products,
        { ...currentProduct, isMandatory: true },
      ];
      checkIfAllProductsAreMandatory(nextProducts);
      setProducts(nextProducts);
      setProductsEdited(
        hasProductChanges(nextProducts, offering?.offeringProducts),
      );
      setValue(
        'offeringProducts',
        nextProducts.map((product: ProductResWithMandatory, index: number) => ({
          productId: product.id,
          isMandatory: product.isMandatory ?? true,
          quantityDisplayType:
            product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
          isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
          position: index + 1,
        })),
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
      setEditable(false);
    }
  };

  const handleProductRemove = (currentProduct: IProductResSchema) => {
    const nextProducts = products.filter(
      (product: IProductResSchema) => product.id !== currentProduct.id,
    );
    setProducts(nextProducts);
    if (nextProducts.length < 1) {
      setProductBucket(undefined);
    }
    setProductsEdited(
      hasProductChanges(nextProducts, offering?.offeringProducts),
    );
    checkIfAllProductsAreMandatory(nextProducts);
    setValue(
      'offeringProducts',
      nextProducts.map((product: ProductResWithMandatory, index: number) => ({
        productId: product.id,
        isMandatory: product.isMandatory,
        quantityDisplayType:
          product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
        isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
        position: index + 1,
      })),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );
  };

  /**
   * Formats and updates the products for the form state.
   * @param productsToUpdate Array of products to be formatted and set in the form.
   */
  const transformMandatoryProducts = (
    productsToUpdate: ProductResWithMandatory[],
  ) => {
    const formattedProducts = productsToUpdate.map((product, index) => ({
      productId: product.id,
      isMandatory: product.isMandatory,
      quantityDisplayType:
        product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
      isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
      position: index + 1,
    }));

    setValue('offeringProducts', formattedProducts, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  /**
   * Handles the mandatory checkbox for a product.
   * @param productsToUpdate Array of products to be formatted and set in the form.
   */
  const handleMandatoryCheckbox = (
    productToUpdate?: ProductResWithMandatory,
  ) => {
    setProducts((prevProducts) => {
      const updatedProducts = prevProducts.map(
        (product: ProductResWithMandatory) => {
          if (productToUpdate && product.id === productToUpdate.id) {
            return {
              ...product,
              quantityDisplayType:
                product.quantityDisplayType ??
                ProductQuantityDisplayTypeEnum.INPUT,
              isMandatory: !product.isMandatory,
            };
          }
          return {
            ...product,
            quantityDisplayType:
              product.quantityDisplayType ??
              ProductQuantityDisplayTypeEnum.INPUT,
            isMandatory: productToUpdate
              ? product.isMandatory
              : !isAllProductMandatory,
          };
        },
      );
      checkIfAllProductsAreMandatory(updatedProducts);
      transformMandatoryProducts(updatedProducts);
      return updatedProducts;
    });
  };

  const handleAddNewRate = () => {
    setEditable(false);
    if (
      isDirty &&
      isValid &&
      productsMatchOfferingType(products, offeringType)
    ) {
      handleSaveButtonPress();
    }
    if (isValid && !errors.name) {
      const uid = `new-${uuidv4()}`;
      setRateIds((prevValue) => [...prevValue, uid]);
      setVisibleRateIds((prevValue) => [...prevValue, uid]);
      setRateTempUUIds((prevValue) => [...prevValue, uid]);
    }
  };

  /**
   * Ensure that we can open the import rate modal
   * if the offering is invalid, then we cannot import as there will be other save issues
   */
  const handleOpenImportRateModal = (): boolean => {
    setEditable(false);
    if (
      isDirty &&
      isValid &&
      productsMatchOfferingType(products, offeringType)
    ) {
      handleSaveButtonPress();
    }
    return isValid && !errors.name;
  };

  const handleImportNewRates = async (
    importedRates: IRateReqSchemaUI[],
    hasCompatibleRates: boolean,
  ) => {
    try {
      setEditable(false);

      // If there are unsaved changes to the offering, save them first
      if (isDirty) {
        await handleSubmit(onSubmit)();
      }

      if (hasCompatibleRates) {
        // Compatible rates were already saved in ImportRateModal, just fetch the updated offering
        await fetchOffering();
      } else {
        // No compatible rates found, so only update the UI with imported rate data
        const tempRateIds = importedRates
          .map((newRate) => newRate.id)
          .filter((rateId): rateId is string => Boolean(rateId));
        setRateIds((prevIds) => [...prevIds, ...tempRateIds]);
        setVisibleRateIds((prevValue) => [...prevValue, ...tempRateIds]);
        setRateTempUUIds((prevIds) => [...prevIds, ...tempRateIds]);

        setRateCloneData((prevCloneData) => {
          const newCloneData = { ...prevCloneData };
          importedRates.forEach((newRate) => {
            if (newRate.id) {
              newCloneData[newRate.id] = { ...newRate };
            }
          });
          return newCloneData;
        });
      }
    } catch (importError) {
      handleApiErrorToast(importError);
    }
  };

  const handleCloneRate = (data: IRateReqSchemaUI) => {
    const uid = `clone-${uuidv4()}`;
    setRateCloneData((prevCloneData) => ({
      ...prevCloneData,
      [uid]: { ...data, id: uid },
    }));
    setRateIds((prevValue) => [...prevValue, uid]);
    setVisibleRateIds((prevValue) => [...prevValue, uid]);
    setRateTempUUIds((prevValue) => [...prevValue, uid]);
  };

  const handleClearClonedRate = (rateId: string) => {
    setRateCloneData((prevCloneData) => {
      prevCloneData = { ...prevCloneData };
      delete prevCloneData[rateId];
      return prevCloneData;
    });
  };

  const handleDirtyRateIds = useCallback(
    (rateId: string, type: 'add' | 'remove') => {
      if (type === 'add') {
        setDirtyRateIds((prevRateIds) => {
          if (!prevRateIds.includes(rateId)) {
            return prevRateIds.concat(rateId);
          }
          return prevRateIds;
        });
      }

      if (type === 'remove') {
        setDirtyRateIds((prevRateIds) => {
          if (prevRateIds.includes(rateId)) {
            return prevRateIds.filter((currId) => currId !== rateId);
          }
          return prevRateIds;
        });
      }
    },
    [],
  );

  // Function to update products state
  const handleReorder = useCallback(
    (newProducts: ProductResWithMandatory[]) => {
      const updatedProducts = newProducts.map((product, index) => ({
        ...product,
        position: index + 1,
      }));
      setProducts(updatedProducts);

      // Update form state
      setValue(
        'offeringProducts',
        updatedProducts.map((product) => ({
          productId: product.id,
          isMandatory: product.isMandatory ?? true,
          position: product.position,
          quantityDisplayType:
            product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
          isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
        })),
        {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        },
      );
    },
    [setValue],
  );

  const handleSaveReorderedProducts = useCallback(async () => {
    if (!offering?.id) {
      return;
    }

    try {
      await updateOffering({
        id: offering.id,
        payload: {
          ...getValues(),
          offeringProducts: products.map((product) => ({
            productId: product.id,
            isMandatory: product.isMandatory,
            position: product.position,
            quantityDisplayType:
              product.quantityDisplayType ??
              ProductQuantityDisplayTypeEnum.INPUT,
            isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
          })),
        },
      });
    } catch (err) {
      handleApiErrorToast(err);
    }
  }, [offering, products, updateOffering, getValues]);

  const mapOfferingProducts = (offeringProducts: IOfferingProductSchema[]) => {
    return offeringProducts?.map((product: IOfferingProductSchema) => ({
      productId: product?.product?.id,
      isMandatory: product?.isMandatory,
      quantityDisplayType:
        product?.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
      isPriceTierLockEligible: product?.isPriceTierLockEligible ?? false,
      position: product?.position,
    }));
  };

  const hasProductChanges = (
    nextProducts: ProductResWithMandatory[],
    offeringProducts?: IOfferingProductSchema[],
  ): boolean => {
    if (!offeringProducts) {
      return nextProducts.length > 0;
    }
    const existing = mapOfferingProducts(offeringProducts);
    const current = nextProducts.map((product) => ({
      productId: product.id,
      isMandatory: product.isMandatory ?? true,
      quantityDisplayType:
        product.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
      isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
      position: product.position,
    }));
    const sortFn = (
      a: { position?: number; productId?: string },
      b: { position?: number; productId?: string },
    ) =>
      (a.position ?? 0) - (b.position ?? 0) ||
      (a.productId ?? '').localeCompare(b.productId ?? '');
    return (
      JSON.stringify([...existing].sort(sortFn)) !==
      JSON.stringify([...current].sort(sortFn))
    );
  };

  const { isReadOnly, inputVariant } = useSettingsReadOnlyRule();

  useEffect(() => {
    if (id) {
      setPageTitle(isReadOnly ? 'View Offering' : 'Edit Offering');
    } else {
      setPageTitle('New Offering');
    }
  }, [id, isReadOnly]);

  useEffect(() => {
    if (id && isError && !isFetching) {
      getAppStore().set(activeToastState, {
        life: 5000,
        severity: 'error',
        summary: 'Error',
        detail: 'Offering does not exist',
      });
      navigate(OFFERING_LIST, { replace: true });
    }
  }, [id, isError, isFetching, navigate]);

  useEffect(() => {
    if (offering?.id && isFirstLoad.current) {
      setEditable(false);
      isFirstLoad.current = false;
    }
  }, [offering?.id]);

  const sortRateIds = useCallback(
    (
      currentRateIds: string[],
      sortItem: RateSortKey,
      order: 'asc' | 'desc',
    ) => {
      const rates = currentRateIds
        .map((rateId) => rateFormRefs.current[rateId]?.getRate())
        .filter(Boolean) as IRateReqSchemaUI[];
      const newOrder = sortRatesByFilterValue(rates, sortItem, order);
      setRateIds(newOrder.map((rate) => rate.id as string));
      // ensure visible rate ids is sorted but values are not modified
      setVisibleRateIds((prevValue) => {
        const existingIds = new Set(prevValue);
        return newOrder
          .filter((rate) => existingIds.has(rate.id))
          .map((rate) => rate.id);
      });
    },
    [],
  );

  const filterRateIdsBySearchTerm = useCallback(
    (searchTerm: string, currentRateIds: string[]) => {
      searchTermRef.current = searchTerm;
      if (!searchTerm?.trim()) {
        setVisibleRateIds(currentRateIds);
        return;
      }
      const tempIds = new Set(rateTempUUIdsRef.current);
      const rates = currentRateIds
        .map((rateId) => rateFormRefs.current[rateId]?.getRate())
        .filter(
          (rate) => rate?.id && !tempIds.has(rate?.id),
        ) as IRateReqSchemaUI[];

      // keep all temp ids and keep them at the end
      setVisibleRateIds([
        ...filterObjectBySearchTerm(searchTerm, rates, 'name').map(
          (rate) => rate.id!,
        ),
        ...tempIds,
      ]);
    },
    [],
  );

  useEffect(() => {
    if (offering?.id) {
      if (!dirtyFields?.offeringProducts && offering.offeringProducts.length) {
        const tempProducts = offering.offeringProducts.map(
          (product: IOfferingProductSchema) => ({
            ...product.product,
            isMandatory: product.isMandatory,
            position: product.position,
            quantityDisplayType: product.quantityDisplayType,
            isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
          }),
        );
        checkIfAllProductsAreMandatory(tempProducts);
        setProducts(sortByProductPosition(tempProducts));
      }

      const offeringRateIds = sortRatesByFilterValue(
        (offering.rates || []).filter(
          (rate: IRateResBaseSchema) =>
            rate.rateType !== RateTypeEnum.ACCOUNT &&
            rate.status !== RateStatusEnum.INACTIVE,
        ),
        selectedSortItemRef.current.value as RateSortKey,
        selectedSortItemRef.current.order,
      ).map((rate: IRateResBaseSchema) => rate.id as string);

      const offeringProducts = mapOfferingProducts(offering.offeringProducts);

      // Only reset form state if offeringProducts hasn't been modified locally
      if (!dirtyFields?.offeringProducts && !productsEdited) {
        reset({
          ...offering,
          // Since description field can be null/undefined setting it as empty string
          // to make it easier to handle dirty state. Eventually it will be null if empty when we make api calls
          description: offering.description ?? '',
          offeringProducts,
        });
      }

      // add with existing rateIds with the response rate ids and maintain the order and data
      const merged = [...offeringRateIds, ...rateTempUUIdsRef.current];
      const uniqueMerged = Array.from(new Set(merged));

      const orderMap: Record<string, number> = {};
      rateIdsRef.current.forEach((value, idx) => (orderMap[value] = idx));

      setRateIds(uniqueMerged);
      // Apply search after we have fetch new rates
      if (searchTermRef.current) {
        filterRateIdsBySearchTerm(searchTermRef.current, rateIdsRef.current);
      } else {
        setVisibleRateIds(uniqueMerged);
      }

      const offeringInactiveRateIds = offering.rates
        ?.filter(
          (rate: IRateResBaseSchema) =>
            rate.rateType !== RateTypeEnum.ACCOUNT &&
            rate.status === RateStatusEnum.INACTIVE,
        )
        ?.sort(
          (a, b) =>
            new Date(a?.modifyDate).valueOf() -
            new Date(b?.modifyDate).valueOf(),
        )
        ?.map((rate: IRateResBaseSchema) => rate.id as string);
      setInactiveRateIds(offeringInactiveRateIds);
    }
  }, [
    offering,
    offering?.rates?.length,
    reset,
    productsEdited,
    dirtyFields?.offeringProducts,
    filterRateIdsBySearchTerm,
  ]);

  useEffect(() => {
    if (offering?.offeringProducts) {
      const existingProducts = mapOfferingProducts(offering.offeringProducts);
      const currentProducts = products.map((product) => ({
        productId: product.id,
        isMandatory: product.isMandatory,
        quantityDisplayType: product.quantityDisplayType,
        isPriceTierLockEligible: product.isPriceTierLockEligible ?? false,
        position: product.position,
      }));

      if (
        JSON.stringify(existingProducts) === JSON.stringify(currentProducts) &&
        productsEdited
      ) {
        setProductsEdited(false);
        reset({
          ...getValues(),
          offeringProducts: currentProducts,
        });
      }
    }
  }, [products, offering, productsEdited, reset, getValues]);

  useEffect(() => {
    if (error) {
      if (offering?.products?.length) {
        setProducts(
          sortByProductPosition(
            offering.offeringProducts.map(
              (product: IOfferingProductSchema) => ({
                ...product.product,
                isMandatory: product.isMandatory,
                position: product.position,
                isPriceTierLockEligible:
                  product.isPriceTierLockEligible ?? false,
              }),
            ),
          ),
        );
      }
    }
  }, [error, offering?.products, offering?.offeringProducts]);

  useCtrlEnterHotkey(() => {
    handleSaveButtonPress();
  });

  const isOfferingArchived = offering?.status === OfferingStatusEnum.ARCHIVED;
  const isOfferingLocked = offering?.locked;
  const offeringHasActiveRates =
    offering?.rates?.some((rate) => rate?.status !== RateStatusEnum.INACTIVE) ??
    false;

  const [searchParams, _] = useSearchParams();
  // Copy offering
  const copyOfferingId = searchParams.get('copyOfferingId');
  const [loadingCopyOffering, setLoadingCopyOffering] = useState(false);

  useEffect(() => {
    if (copyOfferingId) {
      const inactiveProducts = products
        .filter((product) => product.status === ProductStatusEnum.INACTIVE)
        .map((product) => product.id);

      if (
        inactiveProducts &&
        getValues('offeringProducts').some((product) =>
          inactiveProducts.includes(product.productId),
        )
      ) {
        setCopiedOfferingHasInactiveProducts(true);
      } else {
        setCopiedOfferingHasInactiveProducts(false);
      }
    }
  }, [copyOfferingId, getValues('offeringProducts'), products]);

  useEffect(() => {
    (async () => {
      if (!copyOfferingId) {
        return;
      }

      if (
        copyOfferingId?.startsWith('offr_') ||
        copyOfferingId?.startsWith('offer_')
      ) {
        setLoadingCopyOffering(true);
        try {
          const { customId, status, ...offeringTobeCloned } =
            await doGetOfferingById(copyOfferingId);

          const offeringProductsById =
            offeringTobeCloned.offeringProducts.reduce(
              (
                acc: Record<string, IOfferingProductSchema>,
                current: IOfferingProductSchema,
              ) => {
                acc[current.product.id] = current;
                return acc;
              },
              {},
            );

          if (offeringTobeCloned.products?.length) {
            setProducts(
              sortByProductPosition(
                offeringTobeCloned.products.map(
                  (product): ProductResWithMandatory => ({
                    ...product,
                    isMandatory:
                      offeringProductsById[product.id]?.isMandatory ?? true,
                    position: offeringProductsById[product.id]?.position,
                    quantityDisplayType:
                      offeringProductsById[product.id]?.quantityDisplayType ??
                      ProductQuantityDisplayTypeEnum.INPUT,
                    isPriceTierLockEligible:
                      offeringProductsById[product.id]
                        ?.isPriceTierLockEligible ?? false,
                  }),
                ),
              ),
            );
          }

          reset({
            ...offeringTobeCloned,
            status: OfferingStatusEnum.ACTIVE,
          });
          setTimeout(() => {
            const offeringProducts = mapOfferingProducts(
              offeringTobeCloned.offeringProducts,
            );

            setValue('name', `Copy of ${offeringTobeCloned.name}`, {
              shouldDirty: true,
              shouldValidate: true,
            });

            setValue('offeringProducts', offeringProducts, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }, 10);
        } catch (err) {
          handleApiErrorToast(err);
        }
        setLoadingCopyOffering(false);
        // Don't set editable to false for cloned offerings to allow editing
      }
    })();
  }, [copyOfferingId, reset, setValue]);

  const loadingData = loading || loadingCopyOffering;

  const isProductsTableEditable =
    !isReadOnly &&
    !isOfferingArchived &&
    !isOfferingLocked &&
    !offeringHasActiveRates;

  if (isFetching) {
    return <MPageLoader />;
  }

  const visibleIds = new Set(visibleRateIds);

  const renderOfferingTypeMismatchWarning = () => {
    // Check if products exist but don't match the selected offering type
    if (products.length === 0) {
      return null;
    }

    const hasSubscriptionTypeMismatch =
      offeringType === OfferingTypesEnum.SUBSCRIPTION &&
      !productsMatchOfferingType(products, OfferingTypesEnum.SUBSCRIPTION);

    const hasTokenTypeMismatch =
      offeringType === OfferingTypesEnum.TOKEN &&
      !productsMatchOfferingType(products, OfferingTypesEnum.TOKEN);

    if (hasSubscriptionTypeMismatch) {
      return (
        <MAlert
          data-testid="offering-type-mismatch-warning"
          type="warning"
          mb={2}
        >
          You need to add a Subscription Product Type before you can add Rates,
          or change the Offering Type to Onetime.
        </MAlert>
      );
    }

    if (hasTokenTypeMismatch) {
      return (
        <MAlert
          data-testid="offering-type-mismatch-warning"
          type="warning"
          mb={2}
        >
          You need to add a Token Product Type before you can add Rates, or
          change the Offering Type to Onetime Token.
        </MAlert>
      );
    }

    return null;
  };

  return (
    <MPageContainer data-testid="offering-form" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title={getHeaderTitle({
          isReadOnly,
          id: offering?.id,
          title: 'Offering',
        })}
        id={offering?.id}
        copyUrl
        status={
          offering?.status ? OFFERING_STATUS_DISPLAY[offering.status] : ''
        }
        recordAuditInfo={offering}
        entityJson={offering}
      >
        <MFlex alignItems="center">
          {!!offering?.id && !isSubmitted && (isDirty || productsEdited) && (
            <MAlert
              type="warning"
              alertProps={{ px: 2, py: 1 }}
              alertIconProps={{ width: 4, height: 4 }}
              alertDescriptionProps={{ fontSize: 'sm' }}
              mr="4"
            >
              Unsaved Changes
            </MAlert>
          )}

          {!isReadOnly && (
            <MButton
              variant="primary"
              isLoading={loadingData}
              isDisabled={
                !isValid ||
                !isDirty ||
                (products.length > 0 &&
                  !productsMatchOfferingType(products, offeringType))
              }
              onClick={() => handleSaveButtonPress()}
              type="submit"
              data-testid="save-offering-btn"
            >
              Save
            </MButton>
          )}
          {!isReadOnly && offering?.id && (
            <MBox ml={2}>
              <OfferingActions offering={offering} source="DETAIL" />
            </MBox>
          )}
        </MFlex>
      </MPageHeader>
      <form style={{ width: '100%' }}>
        <MGrid
          w="full"
          templateColumns="repeat(12, 1fr)"
          gap={4}
          mt={10}
          mb={10}
          pl={8}
        >
          <MGridItem colSpan={3}>
            <MFormField error={errors.name} label="Name" isRequired>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <MInput
                    variant={
                      isReadOnly
                        ? inputVariant
                        : !!offering?.id && (isDirty || productsEdited)
                          ? 'unsaved'
                          : 'outline'
                    }
                    placeholder="New Offering"
                    isDisabled={isOfferingArchived}
                    isReadOnly={isReadOnly}
                    {...field}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={3}>
            <MFormField error={errors.description} label="Description">
              <Controller
                name="description"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MInput
                    variant={
                      isReadOnly
                        ? inputVariant
                        : !!offering?.id && dirtyFields.description
                          ? 'unsaved'
                          : 'outline'
                    }
                    isDisabled={isOfferingArchived}
                    isReadOnly={isReadOnly}
                    placeholder="Offering description"
                    value={value || ''}
                    {...rest}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={2}>
            <MFormField error={errors.status} label="Status" isRequired>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    isDisabled={isOfferingArchived}
                    isReadOnly={isReadOnly}
                    placeholder={STATUS_PLACEHOLDER}
                    items={OFFERING_STATUS_FORM_OPTIONS}
                    inputProps={{
                      variant: isReadOnly
                        ? inputVariant
                        : !!offering?.id && dirtyFields.status
                          ? 'unsaved'
                          : 'outline',
                    }}
                    {...field}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={2}>
            <MFormField error={errors.startDate} label="Start Date">
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    isDisabled={isOfferingArchived}
                    isReadOnly={isReadOnly}
                    variant={
                      isReadOnly
                        ? inputVariant
                        : !!offering?.id && dirtyFields.startDate
                          ? 'unsaved'
                          : 'outline'
                    }
                    clearable
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={2}>
            <MFormField error={errors.endDate} label="End Date">
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    minDate={minEndDate}
                    isDisabled={isOfferingArchived}
                    isReadOnly={isReadOnly}
                    variant={
                      isReadOnly
                        ? inputVariant
                        : !!offering?.id && dirtyFields.endDate
                          ? 'unsaved'
                          : 'outline'
                    }
                    clearable
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem colSpan={12}>
            <Controller
              name="customFields"
              control={control}
              render={({ field: { value, onChange } }) => (
                <CustomFieldDataForm
                  entity={CustomFieldEntityEnum.OFFERING}
                  value={value || {}}
                  setValue={(val) => onChange(val)}
                  fieldColSpan={3}
                  mode="accordion"
                  accordionAutoExpanded
                  isDisabled={isOfferingArchived}
                  isReadOnly={isReadOnly}
                />
              )}
            />
          </MGridItem>
        </MGrid>
      </form>
      <OfferingTypeInput
        control={control}
        products={products}
        rateIds={rateIds}
        offeringType={offeringType}
        editable={editable}
        setEditable={setEditable}
        handleSaveButtonPress={handleSaveButtonPress}
        isReadOnly={isReadOnly}
      />
      <DndProvider backend={HTML5Backend}>
        <ProductsTable
          isValid={isValid}
          products={products}
          handleSelect={handleProductSelect}
          handleRemove={handleProductRemove}
          isEditable={isProductsTableEditable}
          offeringType={offeringType}
          isReadOnly={isReadOnly}
          onMandatoryCheckboxChange={handleMandatoryCheckbox}
          onUpdateAdvancedSettingsProducts={
            handleUpdateAdvancedSettingsProducts
          }
          isAllProductMandatory={isAllProductMandatory}
          onReorder={handleReorder}
          onSaveReorderedProducts={handleSaveReorderedProducts}
          showInactiveProductsBanner={copiedOfferingHasInactiveProducts}
          productBucket={productBucket}
        />
      </DndProvider>

      {products.length > 0 && (
        <>
          <MBox mt={8} mb={4} w="full" pl={8}>
            <MText fontSize="lg" fontWeight="bold">
              Rates
            </MText>
            {renderOfferingTypeMismatchWarning()}
            <MText color="tGray.darkPurple" mt={0.5}>
              {products.length > 0
                ? 'Rates define your product pricing. They allow you to offer multiple pricing strategies for the same group of products by using different currencies, tier structures, and billing frequencies.'
                : 'There are no rates available yet. Select a few products to setup rates.'}
            </MText>
            {rateIds.length > 0 && !loadingData && (
              <>
                <MFlex align="center" justifyContent="space-between" mt={1}>
                  <SearchInput
                    width="full"
                    placeholder="Search rates"
                    visibleCount={visibleRateIds.length}
                    totalCount={rateIds.length}
                    showCount
                    onChange={(searchTerm) => {
                      filterRateIdsBySearchTerm(searchTerm, rateIds);
                    }}
                  />
                </MFlex>
                <MFlex mt={2}>
                  <MBox>
                    <OfferingFormRateSortButton
                      selectedItem={selectedSortItem.value as RateSortKey}
                      order={selectedSortItem.order}
                      onChange={(sortItem, order) => {
                        sortRateIds(rateIds, sortItem, order);
                        setSelectedSortItem({ order, value: sortItem });
                      }}
                    />
                  </MBox>

                  <ExpandCollapseButton
                    isExpanded={isExpandedAll}
                    onChange={(allExpanded) => setIsExpandedAll(allExpanded)}
                  />
                </MFlex>
              </>
            )}
          </MBox>

          {offering?.id &&
            rateIds.map((rateId: string) => (
              <RateFormProvider
                key={rateId}
                ref={(ref) => {
                  rateFormRefs.current[rateId] = ref;
                }}
                id={rateId}
                isHidden={!visibleIds.has(rateId)}
                archivedOffering={isOfferingArchived}
                products={products}
                type={offeringType}
                typeId={offering?.id ?? ''}
                rateCloneData={rateCloneData[rateId]}
                clearRateCloneData={() => handleClearClonedRate(rateId)}
                removeRateTempUUId={(savedTempRateId: string) => {
                  setRateTempUUIds((prevIds) =>
                    prevIds.filter(
                      (tempRateId: string) => tempRateId !== savedTempRateId,
                    ),
                  );
                  setRateIds((prevIds) =>
                    prevIds.filter(
                      (tempRateId) => tempRateId !== savedTempRateId,
                    ),
                  );
                  setVisibleRateIds((prevIds) =>
                    prevIds.filter(
                      (tempRateId) => tempRateId !== savedTempRateId,
                    ),
                  );
                }}
                onRemove={async () => {
                  // if an actual rate was deleted, re-fetch the rates from the offering
                  if (!rateTempUUIdsRef.current.includes(rateId)) {
                    await fetchOffering();
                  }
                  setRateIds((prevIds) =>
                    prevIds.filter((value) => value !== rateId),
                  );
                  setVisibleRateIds((prevIds) =>
                    prevIds.filter((value) => value !== rateId),
                  );
                  setRateTempUUIds((prevIds) =>
                    prevIds.filter((tempRateId) => tempRateId !== rateId),
                  );
                }}
                onChange={(tempRateId) => {
                  setRateIds((prevValue) =>
                    prevValue.map((value) =>
                      value === rateId ? tempRateId : value,
                    ),
                  );
                  setVisibleRateIds((prevValue) =>
                    prevValue.map((value) =>
                      value === rateId ? tempRateId : value,
                    ),
                  );
                }}
                handleCloneRate={handleCloneRate}
                handleDirtyRateIds={handleDirtyRateIds}
                isReadOnly={isReadOnly}
                isExpandedAll={isExpandedAll}
                offering={offering}
              />
            ))}

          {!isReadOnly && (
            <MFlex
              w="full"
              gridColumn="2/-1"
              pb={3}
              pl={8}
              align="center"
              justifySelf="flex-start"
              justifyContent="space-between"
            >
              <MTooltip
                label={'First, fill in Offering Name'}
                placement="bottom-end"
                isDisabled={!!offeringNameField}
              >
                {/* Keep a single child: the tooltip trigger renders nothing for
                    several, and `space-between` splits them when it's disabled */}
                <MFlex align="center">
                  <MButton
                    isDisabled={
                      !isValid ||
                      !productsMatchOfferingType(products, offeringType) ||
                      isOfferingArchived
                    }
                    variant="tertiary"
                    fontSize="sm"
                    onClick={handleAddNewRate}
                    p={0}
                    m={0}
                    data-testid="add-new-rate-btn"
                  >
                    + Add New Rate
                  </MButton>
                  {!offering?.rates?.length && (
                    <ImportRateModal
                      offeringId={offering?.id}
                      isDisabled={
                        !isValid ||
                        !productsMatchOfferingType(products, offeringType) ||
                        isOfferingArchived
                      }
                      buttonProps={{ ml: 2 }}
                      offeringType={offeringType}
                      products={products}
                      onOpen={handleOpenImportRateModal}
                      onImportRates={handleImportNewRates}
                    />
                  )}
                </MFlex>
              </MTooltip>
              {isExpandedAll && (
                <ExpandCollapseButton
                  isExpanded={isExpandedAll}
                  onChange={(allExpanded) => setIsExpandedAll(allExpanded)}
                />
              )}
            </MFlex>
          )}

          {offering?.id && inactiveRateIds.length > 0 && (
            <>
              <MDivider />
              <MBox
                w="full"
                gridColumn="2/-1"
                py={3}
                pl={8}
                justifySelf="flex-start"
              >
                <MButton
                  data-testid="show-inactive-rate-btn"
                  variant="tertiary"
                  fontSize="sm"
                  p={2}
                  m={0}
                  onClick={() => setShowInactiveRate(!showInactiveRate)}
                >
                  {showInactiveRate ? 'Hide' : 'Show'} Inactive Rates
                </MButton>
              </MBox>
              {showInactiveRate &&
                inactiveRateIds.map((rateId: string) => (
                  <RateFormProvider
                    key={rateId}
                    id={rateId}
                    archivedOffering={isOfferingArchived}
                    products={products}
                    type={offeringType}
                    typeId={offering.id}
                    rateCloneData={rateCloneData[rateId]}
                    clearRateCloneData={() => handleClearClonedRate(rateId)}
                    removeRateTempUUId={(savedTempRateId: string) =>
                      //remove tempUUId after save/clone
                      setRateTempUUIds(
                        rateTempUUIds.filter(
                          (tempRateId: string) =>
                            tempRateId !== savedTempRateId,
                        ),
                      )
                    }
                    onRemove={async () => {
                      await fetchOffering();
                      setInactiveRateIds(
                        inactiveRateIds.filter((x: string) => x !== rateId),
                      );
                      setRateTempUUIds(
                        rateTempUUIds.filter(
                          (tempRateId: string) => tempRateId !== rateId,
                        ),
                      );
                    }}
                    handleCloneRate={handleCloneRate}
                    handleDirtyRateIds={handleDirtyRateIds}
                    isReadOnly={isReadOnly}
                    isExpandedAll={isExpandedAll}
                    offering={offering}
                  />
                ))}
            </>
          )}
        </>
      )}
    </MPageContainer>
  );
};
